import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { PrimaryButton, SecondaryButton, Form, FieldTextInput, FieldRadioButton } from '../../components';
import { numberAtLeast, required } from '../../util/validators';
import { addFavorite, removeFavorite, getFavoriteListings } from '../../ducks/favorites.duck';
import { addToCart, getCartItems } from '../../ducks/cart.duck';
import css from './InlineOrderForm.module.css';

const InlineOrderForm = props => {
  const {
    formId,
    onSubmit,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    displayDeliveryMethod,
    onFetchTransactionLineItems,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    isOutOfStock,
    setShowOfferModal,
    currentUser,
    history,
    isIframe,
    loginAsGuest,
    selectedDelivery,
    onAddFavorite,
    onRemoveFavorite,
    favoriteListings,
    // Cart-related
    listing,
    authorName,
    onAddToCart,
    cartItems,
  } = props;

  const intl = useIntl();
  const [quantity, setQuantity] = useState(1);
  const hasStock = currentStock && currentStock > 0;
  const isInCart = listingId?.uuid && Array.isArray(cartItems) && cartItems.some(i => i.listingId === listingId.uuid);
  const bothAvailable = !!(shippingEnabled && pickupEnabled);
  const normalizedSelectedDelivery =
    selectedDelivery === 'pickup' && pickupEnabled
      ? 'pickup'
      : selectedDelivery === 'shipping' && shippingEnabled
      ? 'shipping'
      : null;
  const deliveryMethod =
    normalizedSelectedDelivery || (!bothAvailable ? (pickupEnabled ? 'pickup' : 'shipping') : null);
  const canAddToCart = hasStock && (!bothAvailable || !!selectedDelivery);

  const listingUuid = listingId?.uuid;
  const isFavorited = listingUuid && Array.isArray(favoriteListings) && favoriteListings.includes(listingUuid);

  const listingSource = listing?.attributes?.publicData?.source;
  // Partner-sourced listings carry their own shipping model in publicData;
  // anything else falls back to Shippo 'buyer' rates, which cap the order at
  // one unit. BHC belongs here: it ships at a flat fee and the feed has no
  // parcel data for Shippo to quote.
  const isPartnerSourced =
    listingSource === 'epro' || listingSource === 'wri' || listingSource === 'bhc';
  const shippingMethod = isPartnerSourced
    ? listing?.attributes?.publicData?.shippingMethod || 'flat'
    : 'buyer';
  const isBuyerShipping = String(shippingMethod || '').toLowerCase() === 'buyer';
  const shouldLimitToOne = isBuyerShipping && deliveryMethod === 'shipping';
  const effectiveQuantity = shouldLimitToOne ? 1 : quantity;

  useEffect(() => {
    if (shouldLimitToOne && quantity !== 1) {
      setQuantity(1);
    }
  }, [shouldLimitToOne, quantity]);

  const handleWishlistClick = () => {
    if (!currentUser) {
      history.push('/login');
      return;
    }
    if (isFavorited) {
      onRemoveFavorite(listingUuid);
    } else {
      onAddFavorite(listingUuid);
    }
  };

  const handleAddToCart = () => {
    if (!listing || !listingUuid) return;

    const attrs = listing.attributes || {};
    const price = attrs.price;
    const title = attrs.title || '';
    const publicData = attrs.publicData || {};

    // Get image URL from listing images if available
    const images = listing.images || [];
    const firstImage = images[0];
    const imageUrl =
      firstImage?.attributes?.variants?.['square-small']?.url ||
      firstImage?.attributes?.variants?.['square-small2x']?.url ||
      firstImage?.attributes?.variants?.['listing-card']?.url ||
      firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
      firstImage?.attributes?.variants?.['scaled-small']?.url ||
      firstImage?.attributes?.variants?.['scaled-medium']?.url ||
      firstImage?.attributes?.url ||
      publicData?.imageUrls?.[0] ||
      null;

    // Build a slug from the title for the listing URL
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    // flatAmountInCents: only relevant for ePro/WRI flat-rate items
    const flatAmountInCents = isPartnerSourced
      ? (publicData.flatAmountInCents ??
         (publicData.flatShippingAmount ? Math.round(publicData.flatShippingAmount * 100) : 0))
      : 0;

    const cartItem = {
      listingId: listingUuid,
      authorId: listing.author?.id?.uuid || null,
      transactionProcessAlias: publicData.transactionProcessAlias || null,
      unitType: publicData.unitType || 'item',
      title,
      price: price?.amount || 0,
      currency: price?.currency || 'USD',
      imageUrl,
      source: listingSource || null,
      slug,
      sellerName: authorName || '',
      fulfiller: authorName || '',
      quantity: effectiveQuantity,
      currentStock: currentStock ?? null,
      deliveryMethod: deliveryMethod || null,
      shippingMethod,
      flatAmountInCents,
      parcel: publicData.parcelData
        ? {
            length: publicData.parcelData.length || 0,
            width: publicData.parcelData.width || 0,
            height: publicData.parcelData.height || 0,
            weight: publicData.parcelData.weight || 0,
          }
        : null,
      // Shippo items (shippingMethod='buyer') never support pickup regardless of listing config
      pickupEnabled: shippingMethod !== 'buyer' && !!publicData.pickupEnabled,
      shippingEnabled: !!publicData.shippingEnabled,
      shippingOrigin: publicData.shippingAddress || null,
      cost: publicData.cost ? Math.round(parseFloat(publicData.cost) * 100) : 0,
    };

    onAddToCart(cartItem);
  };

  const isAuthenticated = !!currentUser;

  // Guest login handler - submit with current form values
  const handleGuestLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loginAsGuest) {
      const formValues = {
        quantity: effectiveQuantity,
        deliveryMethod: displayDeliveryMethod ? deliveryMethod : undefined,
      };
      loginAsGuest(formValues);
    }
  };

  // Update line items when quantity or delivery method changes
  const handleUpdate = () => {
    if (hasStock && onFetchTransactionLineItems) {
      const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
      onFetchTransactionLineItems({
        orderData: { stockReservationQuantity: effectiveQuantity, ...deliveryMethodMaybe },
        listingId,
        isOwnListing,
      });
    }
  };

  const handleQuantityChange = (e) => {
    if (shouldLimitToOne) {
      setQuantity(1);
      return;
    }
    const newQuantity = parseInt(e.target.value, 10);
    if (newQuantity > 0 && newQuantity <= currentStock) {
      setQuantity(newQuantity);
    }
  };

  const handleFormSubmit = (values) => {
    onSubmit({
      ...values,
      quantity: effectiveQuantity,
      deliveryMethod: displayDeliveryMethod ? deliveryMethod : undefined,
    });
  };

  return (
    <FinalForm
      onSubmit={handleFormSubmit}
      initialValues={{ quantity, deliveryMethod }}
      render={({ handleSubmit, submitting }) => (
        <Form onSubmit={handleSubmit} className={css.root}>
          {/* Quantity stepper */}
          <div className={css.quantityRow}>
            <span className={css.quantityLabel}>Quantity:</span>
            <div className={css.stepper}>
              <button
                type="button"
                className={css.stepperBtn}
                disabled={!hasStock || effectiveQuantity <= 1 || shouldLimitToOne}
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
              >
                −
              </button>
              <span className={css.stepperValue}>{effectiveQuantity}</span>
              <button
                type="button"
                className={css.stepperBtn}
                disabled={!hasStock || effectiveQuantity >= currentStock || shouldLimitToOne}
                onClick={() => setQuantity(q => Math.min(currentStock, q + 1))}
              >
                +
              </button>
            </div>
          </div>
          {shouldLimitToOne ? (
            <p className={css.deliveryHint}>
              This item supports only 1 unit per purchase because shipping is calculated in real time.
            </p>
          ) : null}

          {/* Action Buttons */}
          <div className={css.actions}>
            <PrimaryButton
              type={isAuthenticated ? "submit" : "button"}
              onClick={isAuthenticated ? undefined : handleGuestLogin}
              inProgress={submitting}
              disabled={isOutOfStock || !hasStock}
              className={css.buyButton}
            >
              {hasStock ? 'Buy now' : 'Out of stock'}
            </PrimaryButton>

            {/* hasStock && (
              <>
                {isInCart ? (
                  <a href={'/cart' + (isIframe ? '?iframe=true' : '')} className={css.addToCartBtnDone}>
                    Go to Cart
                  </a>
                ) : (
                  <button
                    type="button"
                    className={css.addToCartBtn}
                    onClick={handleAddToCart}
                    disabled={!canAddToCart}
                  >
                    Add to cart
                  </button>
                )}
                {bothAvailable && !selectedDelivery && !isInCart && (
                  <p className={css.deliveryHint}>Please select a delivery method above</p>
                )}
              </>
            ) */}

            {!isIframe ? (
              <button
                type="button"
                className={css.wishlistButton}
                disabled={isOutOfStock || !hasStock}
                onClick={handleWishlistClick}
              >
                {isFavorited ? 'Saved to wishlist' : 'Save to wishlist'}
              </button>
            ) : null}
          </div>
        </Form>
      )}
    />
  );
};

const mapStateToProps = state => ({
  favoriteListings: getFavoriteListings(state),
  cartItems: getCartItems(state),
});

const mapDispatchToProps = dispatch => ({
  onAddFavorite: listingId => dispatch(addFavorite(listingId)),
  onRemoveFavorite: listingId => dispatch(removeFavorite(listingId)),
  onAddToCart: item => dispatch(addToCart(item)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InlineOrderForm);
